import { BusinessFreeColumnValue } from '@/coreDomainsModule/entities/businessBase';
import { BusinessFreeColumnTableHeaderKey } from '@/coreDomainsModule/table';
import { TableContentHeader, TableContentProperty } from '../interface/tableContentHeaders';

const AGGREGATE_FREE_COLUMN_KEY_PREFIX = 'businessFreeColumnValues-aggregate-';

/**
 * 集計取引自由欄のテーブルヘッダーキーを生成する
 * @param columnNo - 自由欄の列番号
 */
const getAggregateBusinessFreeColumnKey = (columnNo: number): BusinessFreeColumnTableHeaderKey => {
  return `${AGGREGATE_FREE_COLUMN_KEY_PREFIX}${columnNo}`;
};

/**
 * 集計取引自由欄の動的テーブルヘッダーか判定する
 * @param key - テーブルヘッダーキー
 */
export const isAggregateBusinessFreeColumnKey = (key: string): key is BusinessFreeColumnTableHeaderKey => {
  return key.startsWith(AGGREGATE_FREE_COLUMN_KEY_PREFIX);
};

/**
 * APIから取得した自由欄値に合わせて、集計取引自由欄の動的ヘッダーを更新する
 * @param tableContentHeader - テーブルヘッダー定義
 * @param valueGroups - 各行の自由欄値
 * @param insertAfterKey - 自由欄を差し込む基準となるヘッダーキー
 */
export const updateAggregateBusinessFreeColumnTableHeaders = (
  tableContentHeader: TableContentHeader,
  valueGroups: (BusinessFreeColumnValue[] | undefined)[],
  insertAfterKey?: string
): void => {
  if (valueGroups.length === 0) return;

  const headerByColumnNo = new Map<number, string>();
  for (const values of valueGroups) {
    for (const column of values ?? []) {
      if (column.target !== 'aggregate' || !column.isConfigured) continue;
      headerByColumnNo.set(column.columnNo, column.headerName);
    }
  }

  const freeColumnProperties: TableContentProperty[] = Array.from(headerByColumnNo.entries())
    .sort(([columnNoA], [columnNoB]) => columnNoA - columnNoB)
    .map(([columnNo, headerName]) => {
      const key = getAggregateBusinessFreeColumnKey(columnNo);
      return {
        key,
        displayLabel: headerName,
        isVariableWidth: true,
        isNarrowable: false,
        isEditable: false,
        sortOption: { isSortable: false, isAsc: true },
        propertyPath: { paths: [key], propertyType: 'string' },
      };
    });

  const fixedProperties = tableContentHeader.contentProperties.filter((property) => !isAggregateBusinessFreeColumnKey(property.key));
  const headerModifyIndex = fixedProperties.findIndex((property) => property.key === 'headerModify');
  const insertAfterIndex = insertAfterKey ? fixedProperties.findIndex((property) => property.key === insertAfterKey) : -1;
  const insertIndex =
    insertAfterIndex === -1 ? (headerModifyIndex === -1 ? fixedProperties.length : headerModifyIndex) : insertAfterIndex + 1;
  fixedProperties.splice(insertIndex, 0, ...freeColumnProperties);
  tableContentHeader.contentProperties = fixedProperties;
};

/**
 * 自由欄値をNewBaseTableContentが表示できるフラットなプロパティへ変換する
 * @param values - 自由欄値
 */
export const getAggregateBusinessFreeColumnTableFields = (
  values: BusinessFreeColumnValue[] | undefined
): Partial<Record<BusinessFreeColumnTableHeaderKey, string>> => {
  const fields: Partial<Record<BusinessFreeColumnTableHeaderKey, string>> = {};
  for (const column of values ?? []) {
    if (column.target !== 'aggregate' || !column.isConfigured) continue;
    fields[getAggregateBusinessFreeColumnKey(column.columnNo)] = column.value;
  }
  return fields;
};
